import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import type { FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { extractErrorMessage, EMAIL_RE } from "@/shared/utils";
import { useFieldAvailability } from "@/shared/hooks";
import {
  useGetMyOrganizationQuery,
  useUpdateMyOrganizationMutation,
  useUploadOrganizationPhotoMutation,
  useDeleteOrganizationPhotoMutation,
} from "../api";
import type { OrganizationFieldIssue, OrganizationValidationErrorBody } from "../types";
import {
  organizationFormSchema,
  formValuesFromData,
  diffUpdate,
  validatePhotoFile,
  normalizeIban,
  type OrganizationFormValues,
} from "../validation";

export type SaveStatus = "idle" | "saving" | "success" | "error";

export interface SaveState {
  status: SaveStatus;
  message?: string;
}

const EDITABLE_FIELDS: FieldPath<OrganizationFormValues>[] = [
  "email",
  "phone",
  "address_ru",
  "address_kk",
  "director_basis",
  "activity_type",
  "bank_name",
  "iban",
  "bik",
];

function isEditableField(value: string): value is FieldPath<OrganizationFormValues> {
  return (EDITABLE_FIELDS as string[]).includes(value);
}

export function useOrganizationForm() {
  const { t } = useTranslation("MyOrganizationPage");
  const { data, isLoading, error, refetch } = useGetMyOrganizationQuery();

  const [updateMyOrganization, updateStatus] = useUpdateMyOrganizationMutation();
  const [uploadOrganizationPhoto, uploadStatus] = useUploadOrganizationPhotoMutation();
  const [deleteOrganizationPhoto, deleteStatus] = useDeleteOrganizationPhotoMutation();

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [markedForDelete, setMarkedForDelete] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>({ status: "idle" });
  const isSavingRef = useRef(false);

  const form = useForm<OrganizationFormValues>({
    resolver: zodResolver(organizationFormSchema),
    defaultValues: {
      email: "",
      phone: "",
      address_ru: "",
      address_kk: "",
      director_basis: "",
      activity_type: "",
      bank_name: "",
      iban: "",
      bik: "",
    },
  });

  const { reset, handleSubmit, register, formState, setError, getValues, watch, setValue } = form;

  const emailValue = watch("email") || "";
  const emailAvailability = useFieldAvailability({
    resource: "organization",
    field: "email",
    value: emailValue,
    isFormatValid: EMAIL_RE.test(emailValue),
    exclude_id: data?.id,
    enabled: !!data?.id,
  });
  const emailTakenMessage = emailAvailability.state === "taken" ? t("uniqueness.taken.email") : undefined;

  useEffect(
    function syncFormWithData() {
      if (data) reset(formValuesFromData(data));
    },
    [data, reset],
  );

  const onFileSelected = useCallback(
    function onFileSelected(file: File) {
      const validationKey = validatePhotoFile(file);
      if (validationKey) {
        setPhotoError(t(validationKey));
        return;
      }
      setPhotoError(null);
      setMarkedForDelete(false);
      setPendingFile(file);
    },
    [t],
  );

  const onClearPending = useCallback(function onClearPending() {
    setPendingFile(null);
    setPhotoError(null);
  }, []);

  const onMarkDelete = useCallback(function onMarkDelete() {
    setMarkedForDelete(true);
    setPendingFile(null);
    setPhotoError(null);
  }, []);

  const onCancelDelete = useCallback(function onCancelDelete() {
    setMarkedForDelete(false);
  }, []);

  const dismissSaveState = useCallback(function dismissSaveState() {
    setSaveState({ status: "idle" });
  }, []);

  const onImageError = useCallback(
    function onImageError() {
      refetch();
    },
    [refetch],
  );

  const isMutating =
    updateStatus.isLoading || uploadStatus.isLoading || deleteStatus.isLoading;

  const photoDirty = pendingFile !== null || markedForDelete;
  const isDirty = formState.isDirty || photoDirty;

  const applyFieldErrors = useCallback(
    function applyFieldErrors(issues: OrganizationFieldIssue[]) {
      for (const issue of issues) {
        if (isEditableField(issue.field)) {
          setError(issue.field, { message: issue.issue });
        }
      }
    },
    [setError],
  );

  async function onSave(values: OrganizationFormValues) {
    if (isSavingRef.current) return;
    if (!data) return;
    isSavingRef.current = true;
    setSaveState({ status: "saving" });

    try {
      if (markedForDelete && !pendingFile) {
        try {
          await deleteOrganizationPhoto().unwrap();
          setMarkedForDelete(false);
        } catch (err: unknown) {
          setSaveState({ status: "error", message: extractErrorMessage(err, "save.error.text", t) });
          return;
        }
      } else if (pendingFile) {
        try {
          await uploadOrganizationPhoto(pendingFile).unwrap();
          setPendingFile(null);
        } catch (err: unknown) {
          setSaveState({ status: "error", message: extractErrorMessage(err, "save.error.text", t) });
          return;
        }
      }

      const patch = diffUpdate(values, data);
      if (Object.keys(patch).length > 0) {
        if (typeof patch.iban === "string") {
          patch.iban = normalizeIban(patch.iban);
          if (patch.iban === "") patch.iban = null;
        }

        try {
          const latest = await updateMyOrganization(patch).unwrap();
          reset(formValuesFromData(latest));
        } catch (err: unknown) {
          const body = (err as { data?: OrganizationValidationErrorBody })?.data;
          if (body?.details && Array.isArray(body.details) && body.details.length > 0) {
            applyFieldErrors(body.details);
            setSaveState({ status: "error", message: t("save.error.validation") });
            return;
          }
          setSaveState({ status: "error", message: extractErrorMessage(err, "save.error.text", t) });
          return;
        }
      }

      setSaveState({ status: "success" });
    } finally {
      isSavingRef.current = false;
    }
  }

  const submitHandler = handleSubmit(onSave);

  return {
    data,
    isLoading,
    error,
    refetch,
    register,
    watch,
    setValue,
    errors: formState.errors,
    emailTakenMessage,
    isDirty,
    isMutating,
    saveState,
    dismissSaveState,
    submit: submitHandler,
    getValues,
    photo: {
      existingUrl: data?.organization_project_photo_url ?? null,
      pendingFile,
      markedForDelete,
      fileError: photoError,
      onFileSelected,
      onClearPending,
      onMarkDelete,
      onCancelDelete,
      onImageError,
    },
  };
}
